import React, { useEffect, useState } from "react";
import { Card, Title, Flex, Button } from "@tremor/react";
import RulesTable from "./ruletable";
import { Dialog, DialogPanel, Transition, TransitionChild } from "@headlessui/react";
import RuleForm from "./form";
import * as rulesAPI from "../../../services/rule";
import { PlusCircleIcon } from "@heroicons/react/24/outline";

export default function RulesPage({ device }) {
  const [isOpen, setIsOpen] = useState(false);
  const [rules, setRules] = useState([]);
  const closeModal = () => setIsOpen(false);
  const [rule, setRule] = useState({});
  const [formMode, setFormMode] = useState("create");

  const onCreate = async (ruleData, mode) => {
    // call api to create rule
    try {
      let newRule;
      if (mode === "edit") {
        newRule = await rulesAPI.update(rule._id, {
          ...ruleData,
          deviceId: device._id,
          serial: device.serial,
        });
        const newRules = rules.map((r) => {
          if (r._id === rule._id) {
            return newRule;
          }
          return r;
        });
        setRules(newRules);
      } else {
        newRule = await rulesAPI.create({
          ...ruleData,
          deviceId: device._id,
          serial: device.serial,
        });
        setRules([...rules, newRule]);
      }

      closeModal();
    } catch (err) {
      console.log(err);
    }
  };

  const getRules = async () => {
    if (device === undefined && !device._id) return;
    try {
      const rulesResp = await rulesAPI.list(device._id);
      setRules(rulesResp.rows);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    if (device === undefined && device._id === undefined) {
      return;
    }

    getRules();
  }, [device]);

  const handleAddRule = () => {
    setRule({});
    setFormMode("create");
    setIsOpen(true);
  };

  const onDelete = async (ruleId) => {
    try {
      await rulesAPI.remove(ruleId);
      // remove from rules
      const newRules = rules.filter((rule) => rule._id !== ruleId);
      setRules(newRules);
    } catch (err) {
      console.log(err);
    }
  };

  const onEdit = async (toEditRule) => {
    setRule(toEditRule);
    setFormMode("edit");
    setIsOpen(true);
  };

  const onToggleRule = async (rule) => {
    const previousRules = [...rules];
    const updatedRules = rules.map(r =>
      r._id === rule._id ? { ...r, enabled: !r.enabled } : r
    );
    setRules(updatedRules);
    try {
      await rulesAPI.update(rule._id, { ...rule, enabled: !rule.enabled, deviceId: device._id, serial: device.serial });
    } catch (err) {
      setRules(previousRules);
      console.error('Error toggling rule:', err);
    }
  };

  return (
    <div className="mt-5">
      <Card>
        <Flex className="flex-col sm:flex-row gap-4 sm:gap-0">
          <div className="flex-1">
            <Flex justifyContent="start" className="space-x-2">
              <Title className="text-lg sm:text-xl">Rules</Title>
            </Flex>
          </div>
          <div className="flex-shrink-0">
            <Button icon={PlusCircleIcon} onClick={handleAddRule} size="xs" className="w-full sm:w-auto">
              Add Rule
            </Button>
          </div>
        </Flex>
        <RulesTable
          rules={rules}
          onDelete={onDelete}
          onEdit={onEdit}
          onCreate={handleAddRule}
          onToggleRule={onToggleRule}
        />

        <Transition appear show={isOpen}>
          <Dialog as="div" className="relative z-[9999]" onClose={closeModal}>
            <TransitionChild
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-gray-900 bg-opacity-25 z-[9998]" />
            </TransitionChild>
            <div className="fixed inset-0 overflow-y-auto z-[9999]">
              <div className="flex min-h-full items-center justify-center p-2 sm:p-4 text-center">
                <TransitionChild
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <DialogPanel
                    className="w-full max-w-xl transform overflow-hidden ring-tremor bg-white dark:bg-gray-900
                                      p-4 sm:p-6 text-left align-middle shadow-tremor transition-all rounded-xl z-[10000]"
                  >
                    <RuleForm
                      deviceId={device._id}
                      onCancel={closeModal}
                      onAction={onCreate}
                      formMode={formMode}
                      rule={rule}
                    />
                  </DialogPanel>
                </TransitionChild>
              </div>
            </div>
          </Dialog>
        </Transition>
      </Card>
    </div>
  );
}
